import { AccountLayout } from "@/components/AccountLayout";
import { DealCard } from "@/components/DealCard";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useFavorites } from "@/hooks/useFavorites";
import { useProducts } from "@/hooks/useProducts";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Scale, CheckCircle2 } from "lucide-react";

export default function Favorites() {
  const { data: favorites = [], isLoading: favoritesLoading } = useFavorites();
  const { data: allProducts = [], isLoading: productsLoading } = useProducts();
  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
  const navigate = useNavigate();

  const favoriteProducts = allProducts.filter(product =>
    favorites.some(fav => fav.product_id === product.id)
  );

  const handleToggleSelection = (productSlug: string) => {
    setSelectedForComparison(prev =>
      prev.includes(productSlug)
        ? prev.filter(slug => slug !== productSlug)
        : prev.length < 4
        ? [...prev, productSlug]
        : prev
    );
  };

  const handleCompare = () => {
    if (selectedForComparison.length >= 2) {
      navigate(`/compare?ids=${selectedForComparison.join(",")}`);
    }
  };

  const isLoading = favoritesLoading || productsLoading;

  return (
    <AccountLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">My Favorites</h1>
            <p className="text-muted-foreground">
              Products you've saved for later
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading favorites...</p>
          </div>
        ) : favoriteProducts.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No favorites yet</h3>
            <p className="text-muted-foreground mb-6">
              Start browsing and save products you're interested in. Heart 2-4 products to compare them side-by-side.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button onClick={() => navigate("/browse")}>Browse Products</Button>
              <Button 
                variant="outline" 
                onClick={() => navigate("/ask-circle")}
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                <span className="flex items-baseline gap-1">
                  <span>Need help choosing? Ask</span>
                  <span className="font-handelson font-light text-base -ml-0.5">Circle</span>
                </span>
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Always visible instructional banner */}
            <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Scale className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">
                    Compare Your Favorites Side-by-Side
                  </h3>
                  <p className="text-sm text-blue-700">
                    Select 2-4 products using the checkboxes to see detailed comparisons based on pricing, features, and integrations.
                  </p>
                </div>
              </div>
            </div>

            {/* Selection feedback banner */}
            {selectedForComparison.length > 0 && (
              <div className="bg-green-50 border-l-4 border-green-600 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-900">
                      {selectedForComparison.length} product{selectedForComparison.length !== 1 ? 's' : ''} selected
                    </span>
                    {selectedForComparison.length === 1 && (
                      <span className="text-sm text-green-700">- Select 1-3 more to compare</span>
                    )}
                  </div>
                  {selectedForComparison.length >= 2 && (
                    <Button onClick={handleCompare} size="sm" className="bg-green-600 hover:bg-green-700">
                      Compare Now ({selectedForComparison.length})
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {favoriteProducts.map(product => (
                <div 
                  key={product.id} 
                  className="relative group hover:ring-2 hover:ring-blue-200 rounded-lg transition-all"
                >
                  {/* Comparison checkbox with label */}
                  <div className="absolute top-2 left-2 z-10 bg-background/95 rounded-md px-2 py-1.5 shadow-sm border border-border group-hover:border-blue-600 group-hover:bg-blue-50 transition-all">
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium">
                      <Checkbox
                        checked={selectedForComparison.includes(product.slug)}
                        onCheckedChange={() => handleToggleSelection(product.slug)}
                        disabled={
                          !selectedForComparison.includes(product.slug) &&
                          selectedForComparison.length >= 4
                        }
                      />
                      <span className="text-foreground">Compare</span>
                    </label>
                  </div>
                  <DealCard {...product} isFromDatabase={true} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AccountLayout>
  );
}
